import { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2, Handshake, Wrench, GraduationCap, Truck, TrendingUp,
  MessageCircle, CheckCircle2, AlertCircle,
} from "lucide-react";
import { api, formatApiError } from "@/api";
import { useSite } from "@/context/SiteContext";
import { usePageContent } from "@/context/PageContentContext";
import { buildFranchiseMessage, openWhatsApp } from "@/lib/whatsappMessages";

const STEP_ICONS = [Building2, Handshake, Wrench, GraduationCap, Truck, TrendingUp];

const BUSINESS_TYPES = [
  "Restaurant Owner", "Hotel Owner", "Cafe Owner",
  "Investor", "First-time Entrepreneur", "Existing Cloud Kitchen",
];

export default function FranchiseSolutions() {
  const { content } = usePageContent("franchise");

  return (
    <div data-testid="page-franchise" className="bg-cream">
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-40 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="font-mono-label text-[var(--k-green)] mb-5">{content.hero_eyebrow}</div>
            <h1 data-testid="franchise-headline" className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]">
              {content.hero_title_pre} <em className="green-gradient-text not-italic">{content.hero_title_em}</em> {content.hero_title_post}
            </h1>
            <p className="mt-7 text-lg text-[var(--k-text-2)] max-w-2xl leading-relaxed">
              {content.hero_intro}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-beige" data-testid="franchise-flow">
        <div className="container-k">
          <div className="max-w-3xl mb-14">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.flow_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.flow_title_pre} <em className="green-gradient-text not-italic">{content.flow_title_em}</em> {content.flow_title_post}
            </h2>
          </div>
          <div className="md:grid md:grid-cols-6 md:gap-4 space-y-10 md:space-y-0">
            {(content.steps || []).map((s, i) => {
              const Icon = STEP_ICONS[i % STEP_ICONS.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`franchise-step-${i}`}
                >
                  <div className="w-[68px] h-[68px] rounded-full border-2 border-[var(--k-olive)] bg-[var(--k-card)] flex items-center justify-center mb-5 shadow-md">
                    <Icon className="w-6 h-6 text-[var(--k-green)]" strokeWidth={1.4} />
                  </div>
                  <div className="font-mono-label text-[var(--k-gold)] text-[0.55rem] mb-2">0{i + 1}</div>
                  <h4 className="font-serif-display text-lg text-[var(--k-text)] leading-tight mb-1">{s.label}</h4>
                  <p className="text-[var(--k-muted)] text-xs leading-relaxed">{s.sub}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section bg-cream" data-testid="franchise-perks">
        <div className="container-k grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.perks_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.perks_title_pre} <em className="green-gradient-text not-italic">{content.perks_title_em}</em> {content.perks_title_post}
            </h2>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="franchise-perks-list">
            {(content.perks || []).map((p, i) => (
              <li key={p + i} data-testid={`franchise-perk-${i}`} className="organic-card p-5 flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-[var(--k-green)] text-[var(--k-bg)] flex items-center justify-center font-mono-label text-[0.55rem]">{String(i + 1).padStart(2, "0")}</div>
                <span className="text-[var(--k-text-2)] text-sm">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <FranchiseForm content={content} />
    </div>
  );
}

function FranchiseForm({ content }) {
  const { content: site } = useSite();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "",
    business_type: BUSINESS_TYPES[0], message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/partner", form);
      openWhatsApp(site.whatsapp, buildFranchiseMessage(form));
      setStatus({
        state: "success",
        msg: "Application saved. WhatsApp opened — continue the conversation there.",
      });
      setForm({ name: "", email: "", phone: "", city: "",
        business_type: BUSINESS_TYPES[0], message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <section id="franchise-apply" className="section bg-sand" data-testid="franchise-cta">
      <div className="container-k grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.form_eyebrow}</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
            {content.form_title_pre} <em className="green-gradient-text not-italic">{content.form_title_em}</em>{content.form_title_post}
          </h2>
          <p className="mt-6 text-[var(--k-text-2)] leading-relaxed max-w-md">
            {content.form_intro}
          </p>
        </div>

        <form onSubmit={submit} className="organic-card p-8 md:p-12 lg:col-span-7" data-testid="franchise-form">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Full Name *">
              <input className="kostra-input" required value={form.name} onChange={update("name")} data-testid="franchise-input-name" />
            </Field>
            <Field label="Email *">
              <input className="kostra-input" type="email" required value={form.email} onChange={update("email")} data-testid="franchise-input-email" />
            </Field>
            <Field label="Phone *">
              <input className="kostra-input" type="tel" required value={form.phone} onChange={update("phone")} placeholder="+91 99999 00000" data-testid="franchise-input-phone" />
            </Field>
            <Field label="City *">
              <input className="kostra-input" required value={form.city} onChange={update("city")} data-testid="franchise-input-city" />
            </Field>
            <Field label="I am a *" full>
              <select className="kostra-input cursor-pointer" value={form.business_type} onChange={update("business_type")} data-testid="franchise-select-business" style={{ background: "var(--k-card)" }}>
                {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Tell us more (optional)" full>
              <textarea className="kostra-input resize-none" rows={3} value={form.message} onChange={update("message")} placeholder="Kitchen size, location, capital range…" data-testid="franchise-input-message" />
            </Field>
          </div>

          {status.state === "success" && (
            <div className="mt-8 flex items-start gap-3 border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)] p-4 rounded" data-testid="franchise-form-success">
              <CheckCircle2 className="w-5 h-5 text-[var(--k-green)] mt-0.5" />
              <p className="text-[var(--k-text)] text-sm">{status.msg}</p>
            </div>
          )}
          {status.state === "error" && (
            <div className="mt-8 flex items-start gap-3 border border-red-300 bg-red-50 p-4 rounded" data-testid="franchise-form-error">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-[var(--k-text)] text-sm">{status.msg}</p>
            </div>
          )}

          <button type="submit" disabled={status.state === "loading"} className="btn-green mt-10" data-testid="franchise-submit-btn">
            {status.state === "loading" ? "Submitting…" : (<>Send to WhatsApp <MessageCircle className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}
